import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useAuth } from "../auth/AuthContext";

export interface DemographyItem {
  id: string;
  name: string;
  type: string;
  active: boolean;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
}

interface DemographiesContextType {
  demosResponse: DemographyItem[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const DemographiesContext = createContext<DemographiesContextType | undefined>(undefined);

export const DemographiesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // @ts-ignore
  const { user } = useAuth();
  const [demosResponse, setDemosResponse] = useState<DemographyItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user?.accessToken) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${apiUrl}/users/demographies`, {
        method: "GET",
        headers: { Authorization: `bearer ${user.accessToken}` },
      });
      if (!res.ok) {
        throw new Error("Failed to fetch demographies");
      }
      const data: DemographyItem[] = await res.json();
      setDemosResponse(data || []);
    } catch (e: any) {
      setError(e?.message || "Failed to fetch demographies");
      setDemosResponse([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.accessToken]);

  const value = { demosResponse, isLoading, error, refresh };
  return <DemographiesContext.Provider value={value}>{children}</DemographiesContext.Provider>;
};

export const useDemographies = (): DemographiesContextType => {
  const ctx = useContext(DemographiesContext);
  if (!ctx) {
    throw new Error("useDemographies must be used within a DemographiesProvider");
  }
  return ctx;
};
